"use client"

import { Box, HStack, Icon, SimpleGrid, Stat } from "@chakra-ui/react"
import { Text } from "@chakra-ui/react"
import { type FC, Fragment, useEffect, useMemo, useRef, useState } from "react"

import { type PostStat } from "@/types/database"
import { parseSvgWithCurrentColor } from "@/utils/svgHelpers"

interface ParsedNumericStat {
  finalValueText: string
  fractionDigits: number
  suffix: string
  target: number
  type: "number"
}

interface ParsedTextStat {
  text: string
  type: "text"
}

type ParsedStat = ParsedNumericStat | ParsedTextStat

interface StatsGridProps extends Omit<
  React.ComponentProps<typeof SimpleGrid>,
  "children"
> {
  ariaLabel?: string
  borderTop?: string
  stats?: PostStat[] | null
}

type StatsColumns = NonNullable<StatsGridProps["columns"]>

type StatsGridRenderItem =
  | {
      item: PostStat
      key: string
      type: "stat"
    }
  | {
      key: string
      type: "placeholder"
    }

export const hasStatContent = (stat: PostStat) => {
  return Boolean(
    stat.stat_value?.trim() ||
    stat.stat_title?.trim() ||
    stat.stats_description?.trim()
  )
}

const getKeyedItems = <T,>(
  items: T[],
  getBaseKey: (item: T) => string | null | undefined
) => {
  const counts = new Map<string, number>()

  return items.map((item) => {
    const baseKey = getBaseKey(item)?.trim() || "item"
    const count = (counts.get(baseKey) ?? 0) + 1
    counts.set(baseKey, count)

    return {
      item,
      key: count === 1 ? baseKey : `${baseKey}-${count}`,
    }
  })
}

const getStatKey = (stat: PostStat) =>
  [
    stat.stat_title,
    stat.stat_value,
    stat.stats_description,
    stat.stats_leading_icon,
    stat.stats_trailing_icon,
  ]
    .filter(Boolean)
    .join("|")

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value)

const getColumnsForBreakpoint = (
  columns: StatsColumns,
  breakpoint: "base" | "sm" | "md" | "lg",
  fallback: number
) => {
  if (typeof columns === "number") {
    return columns
  }

  if (!isRecord(columns)) {
    return fallback
  }

  const value = columns[breakpoint]

  return typeof value === "number" && value > 0 ? value : fallback
}

const NUMBER_FORMATTER = new Intl.NumberFormat("en-GB")

const formatStatNumber = (value: number, fractionDigits: number) =>
  fractionDigits > 0
    ? value.toLocaleString("en-GB", {
        maximumFractionDigits: fractionDigits,
        minimumFractionDigits: fractionDigits,
      })
    : NUMBER_FORMATTER.format(value)

const parseStatValue = (stat: string): ParsedStat => {
  const text = stat.trim()
  const match = text.match(/^([+-]?\d[\d,]*(?:\.\d+)?)(.*)$/)

  if (!match) {
    return { text, type: "text" }
  }

  const rawNumber = match[1] ?? ""
  const target = Number(rawNumber.replace(/,/g, ""))

  if (!Number.isFinite(target)) {
    return { text, type: "text" }
  }

  const suffix = (match[2] ?? "").trim()
  const fractionDigits = rawNumber.includes(".")
    ? (rawNumber.split(".")[1]?.length ?? 0)
    : 0

  return {
    finalValueText: formatStatNumber(target, fractionDigits),
    fractionDigits,
    suffix,
    target,
    type: "number",
  }
}

const easeOutExpo = (progress: number) =>
  progress === 1 ? 1 : 1 - Math.pow(2, -10 * progress)

const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches

const AnimatedStatValue = ({
  shouldAnimate,
  value,
}: {
  shouldAnimate: boolean
  value: string
}) => {
  const parsedStat = useMemo(() => parseStatValue(value), [value])
  const [animatedValue, setAnimatedValue] = useState<{
    value: string
    stat: string
  } | null>(null)

  useEffect(() => {
    if (parsedStat.type === "text" || !shouldAnimate) {
      return
    }

    if (prefersReducedMotion()) {
      return
    }

    const duration = 1200
    const start = performance.now()
    let frameId = 0

    const animate = (now: number) => {
      const progress = Math.min((now - start) / duration, 1)
      const easedProgress = easeOutExpo(progress)
      const current = parsedStat.target * easedProgress

      setAnimatedValue({
        stat: value,
        value: formatStatNumber(
          parsedStat.fractionDigits > 0 ? current : Math.round(current),
          parsedStat.fractionDigits
        ),
      })

      if (progress < 1) {
        frameId = requestAnimationFrame(animate)
        return
      }

      setAnimatedValue({
        stat: value,
        value: formatStatNumber(parsedStat.target, parsedStat.fractionDigits),
      })
    }

    frameId = requestAnimationFrame(animate)

    return () => {
      cancelAnimationFrame(frameId)
    }
  }, [parsedStat, shouldAnimate, value])

  if (parsedStat.type === "text") {
    return <>{parsedStat.text}</>
  }

  const animationDisplayValue =
    animatedValue?.stat === value ? animatedValue.value : null
  const displayValue =
    shouldAnimate && !prefersReducedMotion()
      ? (animationDisplayValue ??
        formatStatNumber(0, parsedStat.fractionDigits))
      : parsedStat.finalValueText

  return (
    <>
      <Box as="span" display="inline-flex">
        {displayValue}
      </Box>
      {parsedStat.suffix && (
        <Text
          as="span"
          color="fg.muted"
          display="inline-flex"
          fontSize="0.45em"
          fontStyle="italic"
          lineHeight="1"
          ml={1}
          verticalAlign="super"
        >
          {parsedStat.suffix}
        </Text>
      )}
    </>
  )
}

const StatSvgIcon = ({ svg }: { svg?: string | null }) => {
  if (!svg) return null

  return (
    <Icon
      aria-hidden={true}
      color="fg.muted"
      flexShrink={0}
      focusable={false}
      fontSize="md"
      tabIndex={-1}
    >
      {parseSvgWithCurrentColor(svg)}
    </Icon>
  )
}

const StatsGrid: FC<StatsGridProps> = ({
  ariaLabel = "Stats",
  borderTop = "1px solid {colors.border}",
  columns,
  stats,
  ...props
}) => {
  const statsSectionRef = useRef<HTMLDivElement | null>(null)
  const [shouldAnimateStats, setShouldAnimateStats] = useState(false)
  const visibleStats = Array.isArray(stats) ? stats.filter(hasStatContent) : []
  const keyedStats = getKeyedItems(visibleStats, getStatKey)
  const baseStatsColumns = Math.min(visibleStats.length, 2)
  const mdStatsColumns = Math.min(visibleStats.length, 4)
  const lgStatsColumns = Math.min(visibleStats.length, 6)
  const gridColumns =
    columns ??
    ({
      base: baseStatsColumns,
      md: mdStatsColumns,
      lg: lgStatsColumns,
    } satisfies StatsColumns)
  const baseColumns = getColumnsForBreakpoint(
    gridColumns,
    "base",
    baseStatsColumns
  )
  const smColumns = getColumnsForBreakpoint(gridColumns, "sm", baseColumns)
  const mdColumns = getColumnsForBreakpoint(gridColumns, "md", smColumns)
  const lgColumns = getColumnsForBreakpoint(gridColumns, "lg", mdColumns)
  const placeholderCount =
    columns !== undefined && keyedStats.length % smColumns !== 0
      ? smColumns - (keyedStats.length % smColumns)
      : 0
  const shouldHidePlaceholdersAtBase = baseColumns === 1 && smColumns > 1
  const gridItems: StatsGridRenderItem[] = [
    ...keyedStats.map(({ item, key }) => ({
      item,
      key,
      type: "stat" as const,
    })),
    ...Array.from({ length: placeholderCount }, (_, index) => ({
      key: `placeholder-${index}`,
      type: "placeholder" as const,
    })),
  ]

  useEffect(() => {
    const section = statsSectionRef.current

    if (!section || visibleStats.length === 0) {
      return
    }

    if (!("IntersectionObserver" in window)) {
      const frameId = requestAnimationFrame(() => {
        setShouldAnimateStats(true)
      })

      return () => {
        cancelAnimationFrame(frameId)
      }
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry?.isIntersecting) {
          return
        }

        setShouldAnimateStats(true)
        observer.unobserve(entry.target)
      },
      { threshold: 0.35 }
    )

    observer.observe(section)

    return () => {
      observer.disconnect()
    }
  }, [visibleStats.length])

  if (visibleStats.length === 0) {
    return null
  }

  return (
    <SimpleGrid
      aria-label={ariaLabel}
      as="section"
      borderTop={borderTop}
      columns={gridColumns}
      gap={0}
      ref={statsSectionRef}
      width="100%"
      {...props}
    >
      {gridItems.map((gridItem, index) => {
        const shouldShowMobileEndBorder =
          gridItem.type === "stat" &&
          columns === undefined &&
          baseColumns === 2 &&
          visibleStats.length % baseColumns === 1 &&
          index === visibleStats.length - 1
        const borderLeft = {
          base: index % baseColumns === 0 ? "0" : "1px solid {colors.border}",
          sm: index % smColumns === 0 ? "0" : "1px solid {colors.border}",
          md: index % mdColumns === 0 ? "0" : "1px solid {colors.border}",
          lg: index % lgColumns === 0 ? "0" : "1px solid {colors.border}",
        }
        const borderTop = {
          base: index < baseColumns ? "0" : "1px solid {colors.border}",
          sm: index < smColumns ? "0" : "1px solid {colors.border}",
          md: index < mdColumns ? "0" : "1px solid {colors.border}",
          lg: index < lgColumns ? "0" : "1px solid {colors.border}",
        }

        if (gridItem.type === "placeholder") {
          return (
            <Stat.Root
              aria-hidden={true}
              borderLeft={borderLeft}
              borderTop={borderTop}
              display={{
                base: shouldHidePlaceholdersAtBase ? "none" : "block",
                sm: "block",
              }}
              key={gridItem.key}
              minW={0}
              px={{ base: 5, md: 6 }}
              py={{ base: 5, md: 6 }}
            />
          )
        }

        const stat = gridItem.item
        const trend = stat.stats_up
          ? "up"
          : stat.stats_down
            ? "down"
            : undefined
        const trendColor =
          trend === "up"
            ? "fg.success"
            : trend === "down"
              ? "fg.error"
              : "fg.muted"

        return (
          <Fragment key={gridItem.key}>
            <Stat.Root
              borderLeft={borderLeft}
              // borderRight={{
              //   base: shouldShowMobileEndBorder
              //     ? '1px solid {colors.border}'
              //     : '0',
              //   md: '0',
              // }}
              borderTop={borderTop}
              minW={0}
              px={{ base: 5, md: 6 }}
              py={{ base: 5, md: 6 }}
            >
              {stat.stat_title && (
                <Stat.Label
                  alignItems="center"
                  color="fg.muted"
                  display="flex"
                  fontFamily="mono"
                  gap={2}
                  letterSpacing="0"
                >
                  <StatSvgIcon svg={stat.stats_leading_icon} />
                  {stat.stat_title}
                </Stat.Label>
              )}
              <HStack alignItems="center" gap={2} mt={1}>
                {stat.stat_value && (
                  <Stat.ValueText
                    color="fg"
                    fontSize={{ base: "2xl", md: "3xl" }}
                    fontWeight="600"
                    lineHeight="1"
                  >
                    <AnimatedStatValue
                      shouldAnimate={shouldAnimateStats}
                      value={stat.stat_value}
                    />
                  </Stat.ValueText>
                )}
                {trend === "up" && <Stat.UpIndicator color={trendColor} />}
                {trend === "down" && <Stat.DownIndicator color={trendColor} />}
                <StatSvgIcon svg={stat.stats_trailing_icon} />
              </HStack>
              {stat.stats_description && (
                <Stat.HelpText
                  color="fg.muted"
                  fontSize="sm"
                  lineHeight="1.5"
                  mt={2}
                >
                  {stat.stats_description}
                </Stat.HelpText>
              )}
            </Stat.Root>
            {shouldShowMobileEndBorder && (
              <Stat.Root
                borderLeft={{
                  base: "1px solid {colors.border}",
                  md: "1px solid {colors.border}",
                }}
                borderTop={{
                  base: index < baseColumns ? "0" : "1px solid {colors.border}",
                  md: index < mdColumns ? "0" : "1px solid {colors.border}",
                  lg: index < lgColumns ? "0" : "1px solid {colors.border}",
                }}
                display={{ base: "block", md: "none" }}
                minW={0}
              />
            )}
          </Fragment>
        )
      })}
    </SimpleGrid>
  )
}

export default StatsGrid
